<template>
  <PageHeader
    :title="activeTab === 'Console' ? 'Coding Console' : (entityid ? entity.name : 'Task Worklist')"
    :breadcrumbs="[{ label: 'Portal', to: '/portal' }]"
  >
    <div v-if="entityid && activeTab === 'Worklist'" class="flex items-center">
      <span class="text-sm font-medium text-gray-500 mr-3">Tags:</span>
      <ListInfoTagRelationships
        :key="entity.id"
        :entity-id="entity.id"
        :show-descendants="false"
      />
    </div>
    <template #bottom>
      <Page_Tabs
        :tabs="tabOptions"
        :initial-tab="activeTab"
        class="px-6 pt-2"
        no-border
        @select="onSelectTab"
      />
    </template>
  </PageHeader>

  <!-- Worklist tab -->
  <div v-if="activeTab === 'Worklist'" class="py-8 px-8 space-y-6">
    <!-- Deprecation notice. The Worklist grid is retained ONLY as a fallback for
         when the Console is unavailable; all task actioning should happen in the
         Console. Scoped to this tab so the Console tab stays clean. -->
    <div class="px-4 py-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-900">
      <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="mr-1.5 text-yellow-600" />
      <span class="font-semibold">The Task Worklist is deprecated.</span>
      All coding work must be done in the
      <router-link to="/portal/console" class="underline font-semibold">Console</router-link>
      tab. This worklist remains available as a backup only.
    </div>

    <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
      <ComponentHeader title="Portal Task Summary" />
      <div v-if="entity?.id || !entityid" class="p-6">
        <Task_Overview_Dashboard
          :availableTaskTypes="availableTaskTypes"
          :key="scopeKey"
          ref="taskDashboard"
          :entity="entity"
          @updateTaskTypes="updateTaskTypes"
          @updateStatusFilter="updateStatusFilter"
        />
      </div>
    </div>
    <!-- Default-COLLAPSED: deliberate friction on a deprecated surface, and it
         also stops the AG-Grid server-side datasource firing worklist queries
         nobody asked for. Content is unmounted rather than hidden (the design
         system's performance carve-out) — mounting it would defeat the point. -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
      <ComponentHeader
        v-model:expanded="worklistExpanded"
        title="Portal Task Worklist"
        subtitle="Deprecated — backup only. Use the Console tab."
        collapsible
      />
      <div v-if="worklistExpanded && (entity?.id || !entityid)" class="p-6">
        <TaskList
          :is-unified-worklist="!entityid"
          :key="scopeKey"
          :portal_roles="portal_roles"
          :entity="entityid"
          :initialTaskTypes="initialTaskTypes"
          :initialStatusFilter="initialStatusFilter"
          @submit="reloadDashboard"
        />
      </div>
    </div>
  </div>

  <!-- Console tab — reuses the Coding Console component (self-contained;
       resolves the coder's identity + queue server-side). -->
  <div v-else class="p-6">
    <Coding_Console />
  </div>
</template>

<script>
import TaskList from '@/components/Portal/Task_Tables/TaskList.vue';
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import router from '../../../router';
import Task_Overview_Dashboard from '@/components/Portal/Task_Tables/Task_Overview_Dashboard.vue';
import ListInfoTagRelationships from '@/components/Lists/ListInfoTagRelationships.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import Coding_Console from '@/components/Coding/Coding_Console.vue';

export default {
  components: {
    Task_Overview_Dashboard,
    TaskList,
    ListInfoTagRelationships,
    PageHeader,
    ComponentHeader,
    Page_Tabs,
    Coding_Console,
  },
  props: {
    entityid: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      entity:{},
      availableTaskTypes: [],
      initialTaskTypes: [],
      initialStatusFilter: [],
      // Deprecated worklist starts collapsed; drilling in from a summary card
      // expands it so that interaction still works.
      worklistExpanded: false,

      portal_roles: [],
      // Console first, and bare /portal lands on it: the Worklist is deprecated
      // and retained only as a fallback if the Console is unavailable. See
      // activeTab() for how the Worklist stays reachable.
      tabOptions: [
        { label: 'Console', icon: ['fas', 'headset'] },
        { label: 'Worklist', icon: ['fas', 'list-check'] },
      ],
    };
  },
  computed: {
    ...mapGetters('entity', [
      'availableEntities',
      'current_entity_id',
      'current_entity_name',
    ]),
    ...mapGetters('tenant', ['current_tenant_name']),
    // Remount key for the summary + grid. Keyed on the SCOPE, not on
    // entity?.id: the entity object is fetched async and is stale for the whole
    // window between routes, so keying on it meant switching back to all
    // entities never remounted these children at all. Neither child watches its
    // scope prop — both load in mounted() — so the key is what makes a scope
    // change actually refetch.
    scopeKey() {
      return this.entityid || 'all'
    },
    activeTab() {
      // Bare /portal lands on the CONSOLE — the Worklist is deprecated, so the
      // default entry point is the surface work should happen on. The Worklist
      // is reachable two ways: the explicit /portal/worklist path, and any
      // entity-scoped /portal/:entityid URL (those are practice worklists and
      // must keep working untouched).
      if (this.entityid) return 'Worklist';
      return this.$route.path === '/portal/worklist' ? 'Worklist' : 'Console';
    },
  },
  methods: {
    ...mapActions('entity', ['setEntity_id']),
    onSelectTab(label) {
      // Worklist goes to its own path now, not bare '/portal' (which is the
      // Console). Selecting a tab from an entity-scoped URL leaves the entity
      // behind, as it always has.
      const path = label === 'Console' ? '/portal/console' : '/portal/worklist';
      if (this.$route.path !== path) {
        router.push(path);
      }
    },
    async fetchUserDetails(){
      const response = await axios.get(
        `api/appuserportalroles/`,
      )

      if (response.data) {
        this.portal_roles = response.data;
        // add distinct any task_types in this.portal_roles[...].task_types
        const taskTypesSet = new Set();
        this.portal_roles.forEach(role => {
          if (role.task_types) {
            role.task_types.forEach(taskType => {
              taskTypesSet.add(taskType);
            });
          }
        });
        this.availableTaskTypes = Array.from(taskTypesSet);
        this.initialTaskTypes = Array.from(taskTypesSet);
      }
    },
    async fetchEntityDetails(entityid) {
      axios
        .get(`/api/entities/${entityid}/`)
        .then(response => {
          this.entity = response.data;
        })
        .catch(error => {
          const { status } = error.response;
          if (status === 403) {
            router.push({ name: 'ForbiddenPage' });
          } else if (status === 404) {
            router.push({ name: 'NotFound' });
          } else if (status >= 500) {
            router.push({ name: 'ErrorPage' });
          }
          console.error('Error fetching entity:', error);
        });
    },

    reloadDashboard() {
      this.$refs.taskDashboard.fetchTaskCounts();
    },
    updateTaskTypes(taskTypes) {
      this.initialTaskTypes = taskTypes;
      // Selecting a task type on a summary card is a request to see those rows,
      // so open the (collapsed) worklist rather than appearing to do nothing.
      if (taskTypes.length) {
        this.worklistExpanded = true;
      }
    },
    updateStatusFilter(statuses) {
      this.initialStatusFilter = statuses;
      if (statuses.length) {
        this.worklistExpanded = true;
      }
    },
  },
  watch: {
    entityid(newEntityId) {
      if (newEntityId) {
        this.fetchEntityDetails(newEntityId);
      } else {
        // Scope went back to all entities. The router reuses this component
        // between '/portal/:entityid' and '/portal/worklist' (router-view is
        // unkeyed), so without this the summary keeps posting the entity id of
        // the practice we just left and reports its counts under "All Entities".
        this.entity = {};
      }
    },

  },
  async mounted() {
    await this.fetchUserDetails()
    if(this.entityid){
      await this.fetchEntityDetails(this.entityid);
    }
  },
};
</script>
